import { useCallback, useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2, Mail, ShieldCheck, HardHat, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, beDate, inputCls, btnPrimary } from "@/lib/studioApi";
import { PageSkeleton } from "@/components/studio/StudioUI";

const ROLES = [
  { key: "ouvrier", label: "Ouvrier", icon: HardHat, hint: "Dossiers, devis, interventions, planning. Pas la facturation." },
  { key: "comptable", label: "Comptable", icon: Briefcase, hint: "Factures de vente (PDF + UBL), achats et export comptable, en consultation uniquement." },
];

const RoleBadge = ({ role }) => (
  <span data-testid={`team-role-${role}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${role === "comptable" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-[#E2E7F7] bg-[#F6F8FD] text-[#5C6A87]"}`}>
    {role === "comptable" ? <Briefcase className="h-3 w-3" /> : <HardHat className="h-3 w-3" />} {role === "comptable" ? "Comptable" : "Ouvrier"}
  </span>
);

export default function StudioTeam() {
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "ouvrier" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    studio.get("/team").then(({ data }) => setTeam(data)).catch(() => toast.error("Chargement impossible."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.email.trim()) return;
    setBusy(true);
    try {
      await studio.post("/team/invite", form);
      toast.success(form.role === "comptable" ? "Invitation envoyée à votre comptable." : "Invitation envoyée par email.");
      setForm({ email: "", name: "", role: "ouvrier" });
      load();
    } catch (e) {
      toast.error(apiError(e, "Invitation impossible."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (email) => {
    try {
      await studio.delete(`/team/${encodeURIComponent(email)}`);
      toast.success("Accès retiré.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Suppression impossible."));
    }
  };

  return (
    <StudioLayout
      title="Mon équipe"
      subtitle="Invitez vos ouvriers et votre comptable : chacun a son accès, limité à son rôle. Inclus dans l'abonnement."
    >
      {!team ? (
        <PageSkeleton />
      ) : (
        <div className="mt-6 space-y-4">
          {team.is_owner && (
            <div data-testid="team-invite-card" className="rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
              <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-[#3A5CC8]" /> Donner un accès
              </h2>
              <div data-testid="team-invite-roles" className="mt-4 grid gap-2 sm:grid-cols-2">
                {ROLES.map((r) => (
                  <button key={r.key} type="button" data-testid={`team-invite-role-${r.key}`} onClick={() => setForm({ ...form, role: r.key })} aria-pressed={form.role === r.key}
                    className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${form.role === r.key ? "border-[#3A5CC8] bg-[#F6F8FD]" : "border-[#E2E7F7] bg-white hover:border-[#C9D4F4]"}`}>
                    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${form.role === r.key ? "bg-[#3A5CC8] text-white" : "bg-[#EEF1FC] text-[#3A5CC8]"}`}><r.icon className="h-4 w-4" /></span>
                    <span><span className="block text-sm font-bold text-[#0F2645]">{r.label}</span><span className="block text-xs leading-snug text-[#5C6A87]">{r.hint}</span></span>
                  </button>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_auto] gap-2">
                <input
                  data-testid="team-invite-email"
                  className={inputCls}
                  placeholder={form.role === "comptable" ? "email@fiduciaire.be" : "email@ouvrier.be"}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <input
                  data-testid="team-invite-name"
                  className={inputCls}
                  placeholder={form.role === "comptable" ? "Nom du comptable / fiduciaire" : "Prénom / nom"}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <button type="button" data-testid="team-invite-btn" disabled={busy} onClick={invite} className={btnPrimary}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Inviter
                </button>
              </div>
              <p className="mt-3 text-xs text-[#5C6A87]">
                {form.role === "comptable"
                  ? "Votre comptable reçoit un email, choisit son mot de passe et consulte vos factures, achats et exports quand il en a besoin : plus rien à lui envoyer."
                  : "Il reçoit un email, choisit son mot de passe et accède immédiatement aux dossiers de la société."}
              </p>
            </div>
          )}

          <div data-testid="team-list" className="rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <h2 className="font-heading text-base font-semibold text-[#0F2645]">Accès de la société</h2>
            <div className="mt-4 space-y-2">
              <div data-testid="team-owner" className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-4 py-3">
                <ShieldCheck className="w-4 h-4 text-[#3A5CC8]" />
                <span className="text-sm font-semibold text-slate-800">{team.owner.name || team.owner.email}</span>
                <span className="text-xs text-[#5C6A87]">{team.owner.email}</span>
                <span className="ml-auto rounded-full border border-[#3A5CC8]/20 bg-[#EEF1FC] px-3 py-1 text-[11px] font-bold text-[#3A5CC8]">
                  Responsable
                </span>
              </div>

              {team.members.map((m) => (
                <div key={m.email} data-testid={`team-member-${m.email}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3">
                  <span className="text-sm font-medium text-slate-800">{m.name || m.email}</span>
                  <span className="text-xs text-[#5C6A87]">{m.email}</span>
                  <RoleBadge role={m.member_role || "ouvrier"} />
                  <span className="text-xs text-[#93A0BC]">depuis le {beDate(m.created_at)}</span>
                  {team.is_owner && (
                    <button
                      type="button"
                      data-testid={`team-remove-${m.email}`}
                      onClick={() => remove(m.email)}
                      className="ml-auto text-[#93A0BC] hover:text-red-600 transition-colors"
                      aria-label="Retirer l'accès"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}

              {team.invitations.map((inv) => (
                <div key={inv.email} data-testid={`team-invitation-${inv.email}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  {!!inv.name && <span className="text-sm font-medium text-amber-900">{inv.name}</span>}
                  <span className="text-xs text-amber-800">{inv.email}</span>
                  <RoleBadge role={inv.member_role || "ouvrier"} />
                  <span className="rounded-full border border-amber-300 bg-white px-3 py-1 text-[11px] font-bold text-amber-800">
                    Invitation en attente
                  </span>
                  {team.is_owner && (
                    <button
                      type="button"
                      data-testid={`team-cancel-${inv.email}`}
                      onClick={() => remove(inv.email)}
                      className="ml-auto text-amber-500 hover:text-red-600 transition-colors"
                      aria-label="Annuler l'invitation"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}

              {!team.members.length && !team.invitations.length && (
                <p className="pt-2 text-sm text-[#5C6A87]">
                  Aucun ouvrier ni comptable pour l'instant. Donnez votre premier accès ci-dessus.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
